import { useEffect, useMemo, useRef } from "react";
import type { AppDispatch } from "@redux/store/store";
import { RootState } from "@redux/store/store";
import { useAppSelector } from "@redux/hooks";
import {
  clearNetworkEventsActiveCount,
  setNetworkEventsActiveCountSuccess,
} from "@redux/slices/networkAlertsSlice";
import { ProductInternalName } from "../productConfig";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  fetchNetworkEventsCount,
  parseNetworkEventsActiveCount,
} from "kontrolEdge/components/NetworkAlerts/requests/request";
import { playAlertSound } from "@shared/utils/playAlertSound";

const ALERTS_POLL_INTERVAL_MS = 60_000;

type Params = {
  internalName: string;
  /** Selection slice may be string or number; API requires a numeric tenant id. */
  tenantId: string | number | null | undefined;
  pathname: string;
  dispatch: AppDispatch;
  /**
   * False while a blocking maintenance window is open: the badge is hidden,
   * so polling for a number nobody can see — and could not act on, since the
   * page it links to is replaced — is just a request every minute.
   */
  enabled?: boolean;
};

const toNumericTenantId = (
  tenantId: string | number | null | undefined
): number | undefined => {
  if (tenantId === null || tenantId === undefined) return undefined;
  if (typeof tenantId === "number")
    return Number.isNaN(tenantId) ? undefined : tenantId;
  const n = Number(tenantId);
  return Number.isNaN(n) ? undefined : n;
};

/**
 * Fetches active network-events count for Kontrol (excludes /kontrol/alerts where counts are shown on-page).
 */
export const useKontrolNetworkAlertsForSideMenu = ({
  internalName,
  tenantId,
  pathname,
  dispatch,
  enabled = true,
}: Params) => {
  const api = useAxiosAuth0();
  const networkAlertsState = useAppSelector((state: RootState) => state.networkAlerts);
  const numericTenantId = useMemo(() => toNumericTenantId(tenantId), [tenantId]);
  const alertsActiveCount = useMemo((): number | null => {
    if (numericTenantId == null || networkAlertsState.countTenantId !== numericTenantId) {
      return null;
    }
    return networkAlertsState.activeCount;
  }, [numericTenantId, networkAlertsState]);

  // Tracks the last count we successfully observed for the current tenant.
  // Used to play a notification sound only when the count *increases*
  // between polls (new alerts), not when it stays equal or drops because
  // outages were resolved. Reset on tenant/product change so a higher
  // baseline on a different tenant doesn't trigger a spurious sound.
  const previousCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      !enabled ||
      internalName !== ProductInternalName.KontrolEdge ||
      numericTenantId == null
    ) {
      dispatch(clearNetworkEventsActiveCount());
      previousCountRef.current = null;
      return;
    }
    if (pathname.startsWith("/kontrol/alerts")) {
      return;
    }

    previousCountRef.current = null;
    let cancelled = false;

    const fetchAndCompare = async () => {
      try {
        const res = await fetchNetworkEventsCount(api, { tenantId: numericTenantId });
        if (cancelled) return;
        const parsed = parseNetworkEventsActiveCount(res.data);
        if (parsed === null) return;

        const prev = previousCountRef.current;
        if (prev !== null && parsed > prev) {
          playAlertSound();
        }
        previousCountRef.current = parsed;

        dispatch(
          setNetworkEventsActiveCountSuccess({ tenantId: numericTenantId, active: parsed })
        );
      } catch {
        /* keep last successful Redux count */
      }
    };

    void fetchAndCompare();
    const intervalId = window.setInterval(fetchAndCompare, ALERTS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [api, internalName, numericTenantId, pathname, dispatch, enabled]);

  const showKontrolAlertsMenuBadge = alertsActiveCount !== null && alertsActiveCount > 0;

  return { alertsActiveCount, showKontrolAlertsMenuBadge };
};
